import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    Switch,
    Button,
    Grid,
    Divider,
    InputAdornment,
    Tooltip,
    CircularProgress
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getSageX3Tooltip } from '../../constants/tooltips/sageX3Tooltips';

// Helper component for field with tooltip - matches Email tab pattern
function FieldWithTooltip({ tooltip, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box sx={{ flex: 1 }}>{children}</Box>
            <Tooltip
                title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{tooltip}</Typography>}
                arrow
                placement="top"
            >
                <HelpOutlineIcon
                    fontSize="small"
                    sx={{ color: 'action.active', mt: 1, cursor: 'help' }}
                />
            </Tooltip>
        </Box>
    );
}

function SageConfigSection({ config, onChange, onSave, hasChanges, localisation, onTestConnection, connectionStatus, isSaving }) {
    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                Sage X3 Settings
            </Typography>

            <Grid container spacing={2}>
                {/* Server Configuration */}
                <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Server Configuration
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldWithTooltip tooltip={getSageX3Tooltip('address')}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Server Address"
                            value={config.address || ''}
                            onChange={(e) => onChange('address', e.target.value)}
                            placeholder="e.g., 192.168.1.100:8124"
                            helperText="X3 server address and port"
                            autoComplete="off"
                            inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                        />
                    </FieldWithTooltip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldWithTooltip tooltip={getSageX3Tooltip('folder')}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Sage X3 Folder"
                            value={config.folder || ''}
                            onChange={(e) => onChange('folder', e.target.value)}
                            placeholder="e.g., SEED"
                            helperText="X3 folder name"
                            autoComplete="off"
                            inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                        />
                    </FieldWithTooltip>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.useHttps || false}
                                    onChange={(e) => onChange('useHttps', e.target.checked)}
                                />
                            }
                            label="Use HTTPS"
                        />
                        <Tooltip
                            title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{getSageX3Tooltip('useHttps')}</Typography>}
                            arrow
                        >
                            <HelpOutlineIcon
                                fontSize="small"
                                sx={{ color: 'action.active', ml: -1, cursor: 'help' }}
                            />
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>

            {/* Connected Applications */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                        Connected Applications
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <FieldWithTooltip tooltip={getSageX3Tooltip('clientId')}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Client ID"
                            value={config.clientId || ''}
                            onChange={(e) => onChange('clientId', e.target.value)}
                            autoComplete="off"
                            inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                        />
                    </FieldWithTooltip>
                </Grid>
                <Grid item xs={12}>
                    <FieldWithTooltip tooltip={getSageX3Tooltip('clientSecret')}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Client Secret"
                            type="password"
                            value={config.secret || ''}
                            onChange={(e) => onChange('secret', e.target.value)}
                            autoComplete="new-password"
                            inputProps={{ autoComplete: 'new-password', 'data-form-type': 'other' }}
                        />
                    </FieldWithTooltip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldWithTooltip tooltip={getSageX3Tooltip('user')}>
                        <TextField
                            fullWidth
                            size="small"
                            label="User"
                            value={config.user || ''}
                            onChange={(e) => onChange('user', e.target.value)}
                            autoComplete="new-password"
                            inputProps={{ autoComplete: 'new-password', 'data-form-type': 'other' }}
                        />
                    </FieldWithTooltip>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldWithTooltip tooltip={getSageX3Tooltip('lifetime')}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Token Lifetime"
                            type="number"
                            value={config.lifetime || 600}
                            onChange={(e) => onChange('lifetime', parseInt(e.target.value) || 0)}
                            helperText="Authentication token validity period"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                            }}
                            inputProps={{ min: 60 }}
                        />
                    </FieldWithTooltip>
                </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center' }}>
                <Tooltip
                    title={
                        isSaving
                            ? 'Testing connection and saving, please wait...'
                            : !hasChanges
                            ? 'No changes to save'
                            : ''
                    }
                    arrow
                    disableHoverListener={hasChanges && !isSaving}
                >
                    <span>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onSave}
                            disabled={!hasChanges || isSaving}
                            startIcon={isSaving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                        >
                            {isSaving ? 'Testing & Saving...' : localisation?.["save_button"]?.element_lang || "Save"}
                        </Button>
                    </span>
                </Tooltip>
                <Button
                    variant="outlined"
                    onClick={onTestConnection}
                    disabled={connectionStatus?.loading || isSaving}
                    startIcon={connectionStatus?.loading ? <CircularProgress size={16} /> : null}
                >
                    {connectionStatus?.loading ? 'Testing...' : 'Test Connection'}
                </Button>
            </Box>

        </Box>
    );
}

export default SageConfigSection;
